/**
 * EventSpaces — Editor y visor de "Espacios del evento".
 *
 * Cada evento puede usar varios espacios de un venue por fase
 * (ej: Cóctel en Jardín 2, Banquete en Salón 2, Discoteca en Discoteca 2).
 *
 * Exporta:
 *  - EventSpacesEditor: para el formulario del evento (selector de venue + filas).
 *  - EventSpacesDisplay: visor de solo lectura (detalle, vista del día, portal cliente).
 */
import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  MapPin, Plus, Trash2, MapPinned, Sparkles, FileSearch, Loader2,
  Church, Martini, UtensilsCrossed, Disc3, PartyPopper, Camera, Clock, Music2, RotateCw,
} from 'lucide-react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from './ui/dialog';
import { API_URL } from '../config';

export const DEFAULT_FASES = [
  'Ceremonia', 'Cóctel', 'Banquete', 'Baile/Discoteca', 'Hora Loca', 'Photocall',
];

// Fases típicas de una boda — se autocompletan al elegir venue (si no hay filas)
export const TYPICAL_FASES = ['Ceremonia', 'Cóctel', 'Banquete', 'Baile/Discoteca'];

const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const normalize = (s) => (s || '')
  .toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
  .replace(/[^a-z0-9]+/g, '');

// Mapeo fase → icono + color (lucide, sin emojis). Match por palabra clave normalizada.
const FASE_ICONS = [
  { keys: ['ceremonia', 'boda', 'enlace'], Icon: Church, color: 'text-rose-300', bg: 'bg-rose-500/15' },
  { keys: ['coctel', 'cocktail', 'aperitivo', 'saxo'], Icon: Martini, color: 'text-amber-300', bg: 'bg-amber-500/15' },
  { keys: ['banquete', 'comida', 'cena', 'salon'], Icon: UtensilsCrossed, color: 'text-emerald-300', bg: 'bg-emerald-500/15' },
  { keys: ['baile', 'discoteca', 'disco', 'fiesta'], Icon: Disc3, color: 'text-fuchsia-300', bg: 'bg-fuchsia-500/15' },
  { keys: ['horaloca', 'loca'], Icon: PartyPopper, color: 'text-orange-300', bg: 'bg-orange-500/15' },
  { keys: ['photocall', 'foto', 'photo'], Icon: Camera, color: 'text-sky-300', bg: 'bg-sky-500/15' },
];

export const getFaseIcon = (fase) => {
  const n = normalize(fase);
  const found = FASE_ICONS.find((f) => f.keys.some((k) => n.includes(k)));
  return found || { Icon: MapPin, color: 'text-cyan-300', bg: 'bg-cyan-500/15' };
};

// Servicios/extras que se dan DENTRO de una fase (no son fases en sí) — se detectan
// en el campo libre "nota" de cada fila de espacio y se muestran como insignia.
export const SERVICE_TAGS = [
  { key: 'saxo', label: 'Saxo en directo', keys: ['saxo', 'saxofon'], Icon: Music2, color: 'text-sky-300', bg: 'bg-sky-500/15' },
  { key: 'fotomaton', label: 'Fotomatón', keys: ['fotomaton', 'fotomatón', 'photobooth', 'cabinadefotos'], Icon: Camera, color: 'text-violet-300', bg: 'bg-violet-500/15' },
  { key: '360', label: '360°', insertText: 'Videomatón 360°', keys: ['video360', 'videomaton360', 'plataforma360', 'foto360', 'cabina360', 'photo360'], Icon: RotateCw, color: 'text-orange-300', bg: 'bg-orange-500/15' },
];

export const detectServicios = (nota) => {
  const n = normalize(nota);
  if (!n) return [];
  return SERVICE_TAGS.filter((tag) => tag.keys.some((k) => n.includes(normalize(k))));
};

/* ============== EDITOR ============== */
export const EventSpacesEditor = ({
  espacios = [],
  onChange,
  venueId = '',
  onVenueChange,
  lugarEvento = '',
  playlistId = '',
}) => {
  const [venues, setVenues] = useState([]);
  const [detecting, setDetecting] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [detected, setDetected] = useState([]);      // [{fase, espacio, nota}]
  const [checked, setChecked] = useState([]);        // bool[] paralelo a detected
  const [detectSource, setDetectSource] = useState('');

  // Detecta asignaciones de espacio por fase leyendo el guion del evento con IA.
  // Abre un modal de revisión con checkboxes (no aplica nada hasta confirmar).
  const detectFromGuion = async () => {
    if (!playlistId) return;
    setDetecting(true);
    try {
      const res = await axios.post(
        `${API_URL}/playlists/${playlistId}/extract-spaces`, {},
        { headers: headers() },
      );
      const sugeridos = res.data?.espacios || [];
      if (sugeridos.length === 0) {
        toast.info(res.data?.message || 'No se detectaron espacios en el guion del evento.');
        return;
      }
      setDetected(sugeridos);
      setChecked(sugeridos.map(() => true));
      setDetectSource(res.data?.source || '');
      setReviewOpen(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al leer el guion del evento');
    } finally {
      setDetecting(false);
    }
  };

  const toggleChecked = (idx) => setChecked((prev) => prev.map((c, i) => (i === idx ? !c : c)));
  const allChecked = checked.length > 0 && checked.every(Boolean);
  const toggleAll = () => setChecked(detected.map(() => !allChecked));
  const numSelected = checked.filter(Boolean).length;

  // Aplica SOLO las asignaciones marcadas, con merge inteligente.
  const applySelected = () => {
    const seleccion = detected.filter((_, i) => checked[i]);
    const merged = [...espacios];
    seleccion.forEach((s) => {
      const idx = merged.findIndex(
        (r) => normalize(r.fase) === normalize(s.fase) && !(r.espacio || '').trim(),
      );
      if (idx >= 0) {
        merged[idx] = { ...merged[idx], espacio: s.espacio, nota: merged[idx].nota || s.nota };
      } else if (!merged.some(
        (r) => normalize(r.fase) === normalize(s.fase) && normalize(r.espacio) === normalize(s.espacio),
      )) {
        merged.push({ fase: s.fase, espacio: s.espacio, nota: s.nota });
      }
    });
    onChange(merged);
    setReviewOpen(false);
    toast.success(`${seleccion.length} asignación(es) aplicada(s) — revísalas y guarda el evento.`);
  };

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/venues`, { headers: headers() });
        if (active) setVenues(res.data || []);
      } catch {
        if (active) setVenues([]);
      }
    })();
    return () => { active = false; };
  }, []);

  const selectedVenue = useMemo(
    () => venues.find((v) => v.id === venueId)
      || venues.find((v) => normalize(v.nombre) === normalize(lugarEvento)),
    [venues, venueId, lugarEvento],
  );
  const espacioOptions = selectedVenue?.espacios || [];

  const handleVenueSelect = (id) => {
    const v = venues.find((x) => x.id === id);
    onVenueChange?.(id, v);
    // Si el evento aún no tiene espacios asignados, autocompletar las fases típicas
    // (el DJ solo tendrá que elegir el espacio de cada una).
    if (id && espacios.length === 0) {
      onChange(TYPICAL_FASES.map((fase) => ({ fase, espacio: '', nota: '' })));
    }
  };

  // Añade las fases típicas que aún no estén presentes (botón manual)
  const fillTypicalFases = () => {
    const existing = new Set(espacios.map((r) => normalize(r.fase)));
    const toAdd = TYPICAL_FASES
      .filter((f) => !existing.has(normalize(f)))
      .map((fase) => ({ fase, espacio: '', nota: '' }));
    if (toAdd.length) onChange([...espacios, ...toAdd]);
  };

  const updateRow = (idx, field, val) => {
    const next = espacios.map((row, i) => (i === idx ? { ...row, [field]: val } : row));
    onChange(next);
  };
  const addRow = () => onChange([...espacios, { fase: '', espacio: '', nota: '' }]);
  const removeRow = (idx) => onChange(espacios.filter((_, i) => i !== idx));

  // Añade/quita la etiqueta de un servicio (Fotomatón, 360, Saxo…) del texto libre de "nota"
  const toggleServicio = (idx, tag) => {
    const current = espacios[idx]?.nota || '';
    const active = detectServicios(current).some((s) => s.key === tag.key);
    let next;
    if (active) {
      next = current
        .split(',')
        .map((p) => p.trim())
        .filter((p) => p && !tag.keys.some((k) => normalize(p).includes(normalize(k))))
        .join(', ');
    } else {
      const text = tag.insertText || tag.label;
      next = current ? `${current}, ${text}` : text;
    }
    updateRow(idx, 'nota', next);
  };

  return (
    <div className="space-y-3" data-testid="event-spaces-editor">
      <datalist id="fases-datalist">
        {DEFAULT_FASES.map((f) => <option key={f} value={f} />)}
      </datalist>
      <datalist id="espacios-datalist">
        {espacioOptions.map((e) => <option key={e} value={e} />)}
      </datalist>

      {/* Selector de venue */}
      <div>
        <label className="text-sm text-slate-300 flex items-center gap-1 mb-1">
          <MapPinned className="w-4 h-4 text-cyan-400" /> Venue del catálogo
        </label>
        <select
          value={selectedVenue?.id || ''}
          onChange={(e) => handleVenueSelect(e.target.value)}
          className="w-full p-2 bg-slate-900 border border-slate-700 rounded text-white text-sm"
          data-testid="event-venue-select"
        >
          <option value="">— Sin venue del catálogo —</option>
          {venues.map((v) => (
            <option key={v.id} value={v.id}>{v.nombre}</option>
          ))}
        </select>
        {venues.length === 0 && (
          <p className="text-xs text-slate-500 mt-1">
            No hay venues en el catálogo. Un admin puede crearlos en «Venues».
          </p>
        )}
        {selectedVenue && espacioOptions.length === 0 && (
          <p className="text-xs text-amber-400/80 mt-1">
            «{selectedVenue.nombre}» aún no tiene espacios. Añádelos desde «Venues».
          </p>
        )}
      </div>

      {/* Filas de espacios por fase */}
      <div className="space-y-2">
        {espacios.length === 0 && (
          <p className="text-xs text-slate-500 italic">
            Aún no has asignado espacios. Pulsa «Añadir espacio» para empezar.
          </p>
        )}
        {espacios.map((row, idx) => {
          const { Icon: FaseIcon, color: faseColor } = getFaseIcon(row.fase);
          return (
          <div
            key={idx}
            className="grid grid-cols-1 sm:grid-cols-[auto_1fr_1fr_1.2fr_auto] gap-2 items-center bg-slate-900/60 p-2 rounded border border-slate-700/60"
            data-testid={`event-space-row-${idx}`}
          >
            <div className="hidden sm:flex w-9 h-9 rounded-lg bg-slate-800 items-center justify-center shrink-0">
              <FaseIcon className={`w-4 h-4 ${faseColor}`} />
            </div>
            <Input
              list="fases-datalist"
              value={row.fase || ''}
              onChange={(e) => updateRow(idx, 'fase', e.target.value)}
              placeholder="Fase (Cóctel…)"
              className="bg-slate-800 border-slate-700 text-white text-sm"
              data-testid={`event-space-fase-${idx}`}
            />
            <Input
              list="espacios-datalist"
              value={row.espacio || ''}
              onChange={(e) => updateRow(idx, 'espacio', e.target.value)}
              placeholder="Espacio (Jardín 2…)"
              className="bg-slate-800 border-slate-700 text-white text-sm"
              data-testid={`event-space-espacio-${idx}`}
            />
            <Input
              value={row.nota || ''}
              onChange={(e) => updateRow(idx, 'nota', e.target.value)}
              placeholder="Nota (saxo, fotomatón, 360…)"
              className="bg-slate-800 border-slate-700 text-white text-sm"
              data-testid={`event-space-nota-${idx}`}
            />
            <Button
              type="button" variant="ghost"
              onClick={() => removeRow(idx)}
              className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-9 w-9 p-0 shrink-0"
              data-testid={`event-space-remove-${idx}`}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
            <div className="col-span-1 sm:col-span-5 flex flex-wrap gap-1.5 sm:pl-11 -mt-1">
              {SERVICE_TAGS.map((tag) => {
                const active = detectServicios(row.nota).some((s) => s.key === tag.key);
                return (
                  <button
                    type="button"
                    key={tag.key}
                    onClick={() => toggleServicio(idx, tag)}
                    className={`inline-flex items-center gap-1 text-[11px] rounded-full px-2 py-0.5 border transition ${
                      active ? `${tag.bg} ${tag.color} border-current` : 'bg-slate-800 text-slate-500 border-slate-700 hover:text-slate-300'
                    }`}
                    data-testid={`event-space-servicio-chip-${tag.key}-${idx}`}
                  >
                    <tag.Icon className="w-3 h-3" /> {tag.label}
                  </button>
                );
              })}
            </div>
          </div>
          );
        })}
      </div>

      <div className="flex flex-wrap gap-2">
        <Button
          type="button" variant="outline" onClick={addRow}
          className="border-cyan-700/60 bg-cyan-600/10 text-cyan-300 hover:bg-cyan-600/20"
          data-testid="event-space-add-row"
        >
          <Plus className="w-4 h-4 mr-1" /> Añadir espacio
        </Button>
        <Button
          type="button" variant="outline" onClick={fillTypicalFases}
          className="border-purple-700/60 bg-purple-600/10 text-purple-300 hover:bg-purple-600/20"
          data-testid="event-space-fill-typical"
          title="Añade Ceremonia, Cóctel, Banquete y Baile/Discoteca en un clic"
        >
          <Sparkles className="w-4 h-4 mr-1" /> Fases típicas
        </Button>
        {playlistId && (
          <Button
            type="button" variant="outline" onClick={detectFromGuion} disabled={detecting}
            className="border-emerald-700/60 bg-emerald-600/10 text-emerald-300 hover:bg-emerald-600/20"
            data-testid="event-space-detect-guion"
            title="Lee la info del evento (notas del email/manual) y el guion (PDF/DOC) con IA y propone el espacio de cada fase"
          >
            {detecting
              ? <Loader2 className="w-4 h-4 mr-1 animate-spin" />
              : <FileSearch className="w-4 h-4 mr-1" />}
            {detecting ? 'Leyendo guion…' : 'Detectar del guion (IA)'}
          </Button>
        )}
      </div>

      {/* Modal de revisión de asignaciones detectadas por IA */}
      <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
        <DialogContent
          className="bg-slate-800 border-slate-700 text-white w-[95vw] max-w-lg max-h-[85vh] overflow-y-auto"
          data-testid="detect-spaces-review-dialog"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-white">
              <FileSearch className="w-5 h-5 text-emerald-400" /> Espacios detectados del guion
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {detectSource ? <>Fuente: <span className="text-slate-300">{detectSource}</span>. </> : null}
              Marca las que quieras aplicar. Podrás editarlas después antes de guardar.
            </DialogDescription>
          </DialogHeader>

          <div className="flex items-center justify-between py-1">
            <button
              type="button" onClick={toggleAll}
              className="text-xs text-emerald-300 hover:text-emerald-200 underline"
              data-testid="detect-spaces-toggle-all"
            >
              {allChecked ? 'Desmarcar todas' : 'Marcar todas'}
            </button>
            <span className="text-xs text-slate-400">{numSelected}/{detected.length} seleccionadas</span>
          </div>

          <div className="space-y-2">
            {detected.map((s, idx) => {
              const { Icon, color, bg } = getFaseIcon(s.fase);
              return (
                <label
                  key={idx}
                  className="flex items-center gap-3 bg-slate-900/60 rounded-lg px-3 py-2 border border-slate-700/60 cursor-pointer"
                  data-testid={`detect-space-item-${idx}`}
                >
                  <Checkbox
                    checked={!!checked[idx]}
                    onCheckedChange={() => toggleChecked(idx)}
                    data-testid={`detect-space-check-${idx}`}
                  />
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${bg}`}>
                    <Icon className={`w-4 h-4 ${color}`} />
                  </div>
                  <div className="min-w-0">
                    <span className="text-white text-sm">
                      {s.fase && <strong className={color}>{s.fase}</strong>}
                      {s.fase && s.espacio ? ' · ' : ''}
                      {s.espacio && <span className="font-medium">{s.espacio}</span>}
                    </span>
                    {s.nota && <span className="text-slate-400 block text-xs mt-0.5">📝 {s.nota}</span>}
                  </div>
                </label>
              );
            })}
          </div>

          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              type="button" variant="outline" onClick={() => setReviewOpen(false)}
              className="border-slate-600 bg-slate-700 text-white hover:bg-slate-600"
              data-testid="detect-spaces-cancel"
            >
              Cancelar
            </Button>
            <Button
              type="button" onClick={applySelected} disabled={numSelected === 0}
              className="bg-emerald-500 hover:bg-emerald-600"
              data-testid="detect-spaces-apply"
            >
              Aplicar {numSelected > 0 ? `(${numSelected})` : ''}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// HH:MM -> minutos desde medianoche (null si no es una hora válida)
const horaToMin = (h) => {
  const m = /^(\d{1,2}):(\d{2})$/.exec((h || '').trim());
  if (!m) return null;
  return parseInt(m[1], 10) * 60 + parseInt(m[2], 10);
};

// Construye los segmentos de la línea de tiempo a partir de los bloques de la
// Escaleta que tienen hora Y espacio. Cada segmento "dura" hasta el siguiente.
const buildTimelineSegments = (escaleta) => {
  const valid = (escaleta || [])
    .filter((b) => b && (b.espacio || '').trim() && horaToMin(b.hora) !== null)
    .map((b) => ({ ...b, _min: horaToMin(b.hora) }))
    .sort((a, b) => a._min - b._min);
  return valid.map((b, idx) => ({
    ...b,
    horaFin: valid[idx + 1]?.hora || null,
    servicios: detectServicios(b.nota),
  }));
};

/* ============== VISOR (solo lectura) ============== */
export const EventSpacesDisplay = ({ espacios = [], escaleta = [], compact = false }) => {
  const rows = (espacios || []).filter((r) => r && (r.fase || r.espacio || r.nota));
  const segments = buildTimelineSegments(escaleta);
  // Filas sin horario (no cubiertas por la línea de tiempo) para el listado de respaldo
  const untimedRows = rows.filter(
    (r) => !segments.some((s) => normalize(s.fase) === normalize(r.fase) && normalize(s.espacio) === normalize(r.espacio)),
  );
  if (rows.length === 0 && segments.length === 0) return null;

  if (compact) {
    return (
      <div className="flex flex-wrap gap-1.5" data-testid="event-spaces-display-compact">
        {rows.map((r, idx) => {
          const { Icon, color } = getFaseIcon(r.fase);
          return (
            <span
              key={idx}
              className="inline-flex items-center gap-1 text-xs bg-slate-700/70 text-slate-200 rounded px-2 py-0.5"
            >
              <Icon className={`w-3 h-3 ${color}`} />
              {r.fase ? <strong>{r.fase}:</strong> : null} {r.espacio}
            </span>
          );
        })}
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="event-spaces-display">
      {/* Línea de tiempo por espacio — una sola fila horizontal, ordenada por hora */}
      {segments.length > 0 && (
        <div
          className="flex items-stretch overflow-x-auto pb-1 -mx-1 px-1"
          data-testid="event-spaces-timeline"
        >
          {segments.map((seg, idx) => {
            const { Icon, color, bg } = getFaseIcon(seg.fase || seg.titulo);
            return (
              <div className="flex items-center shrink-0" key={seg.id || idx}>
                {idx > 0 && (
                  <div className="w-5 sm:w-7 h-0.5 bg-gradient-to-r from-slate-600 to-slate-500/60 shrink-0" />
                )}
                <div
                  className={`flex flex-col gap-0.5 rounded-xl border border-white/10 px-3 py-2 min-w-[150px] shrink-0 ${bg}`}
                  data-testid={`event-spaces-timeline-segment-${idx}`}
                >
                  <div className="flex items-center gap-1.5">
                    <Icon className={`w-4 h-4 shrink-0 ${color}`} />
                    <span className={`text-xs font-semibold truncate ${color}`}>
                      {seg.fase || seg.titulo}
                    </span>
                  </div>
                  <span className="text-white text-sm font-medium truncate">{seg.espacio}</span>
                  <span className="text-slate-300 text-xs flex items-center gap-1">
                    <Clock className="w-3 h-3 shrink-0" />
                    {seg.hora}{seg.horaFin ? `–${seg.horaFin}` : ''}
                  </span>
                  {seg.servicios && seg.servicios.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {seg.servicios.map((s) => (
                        <span
                          key={s.key}
                          className={`inline-flex items-center gap-1 text-[10px] ${s.color} ${s.bg} rounded-full px-1.5 py-0.5 w-fit`}
                          data-testid={`event-spaces-timeline-servicio-${s.key}-${idx}`}
                        >
                          <s.Icon className="w-3 h-3" /> {s.label}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Respaldo: fases/espacios sin horario asignado en la Escaleta */}
      {untimedRows.length > 0 && (
        <div className="space-y-1.5" data-testid="event-spaces-display-untimed">
          {segments.length > 0 && (
            <p className="text-[11px] text-slate-500 italic">Sin horario aún:</p>
          )}
          {untimedRows.map((r, idx) => {
            const { Icon, color, bg } = getFaseIcon(r.fase);
            const servicios = detectServicios(r.nota);
            return (
              <div
                key={idx}
                className="flex items-center gap-3 text-sm bg-slate-800/50 rounded-lg px-3 py-2 border border-slate-700/50"
              >
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${bg}`}>
                  <Icon className={`w-5 h-5 ${color}`} />
                </div>
                <div className="min-w-0">
                  <span className="text-white">
                    {r.fase && <strong className={color}>{r.fase}</strong>}
                    {r.fase && r.espacio ? ' · ' : ''}
                    {r.espacio && <span className="font-medium">{r.espacio}</span>}
                  </span>
                  {r.nota && (
                    <span className="text-slate-400 flex items-center gap-1 text-xs mt-0.5">
                      <MapPin className="w-3 h-3 shrink-0" /> {r.nota}
                    </span>
                  )}
                  {servicios.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {servicios.map((s) => (
                        <span
                          key={s.key}
                          className={`inline-flex items-center gap-1 text-[10px] ${s.color} ${s.bg} rounded-full px-1.5 py-0.5 w-fit`}
                        >
                          <s.Icon className="w-3 h-3" /> {s.label}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default EventSpacesEditor;
